import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  Settings,
  Link2,
  Link2Off,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Key
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    fetchGoogleStatus();
  }, []);

  const fetchGoogleStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/status`);
      setGoogleStatus({ ...response.data, loading: false });
    } catch (error) {
      console.error("Error fetching Google status:", error);
      setGoogleStatus({ connected: false, loading: false });
    }
  };

  const handleConnectGoogle = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API}/auth/google/login`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error("Error connecting Google:", error);
      toast.error("Erreur lors de la connexion Google");
      setConnecting(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    setDisconnecting(true);
    try {
      await axios.delete(`${API}/auth/google/disconnect`);
      setGoogleStatus({ connected: false, loading: false });
      toast.success("Compte Google déconnecté");
    } catch (error) {
      console.error("Error disconnecting Google:", error);
      toast.error("Erreur lors de la déconnexion");
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
          Paramètres Admin
        </h1>
        <p className="text-slate-500 mt-1">
          Configuration du compte administrateur
        </p>
      </div>

      {/* Google Business Connection */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Google Business Profile</h2>
              <p className="text-sm text-slate-500">Connexion pour synchroniser les avis clients</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {googleStatus.loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : googleStatus.connected ? (
            <div className="space-y-4">
              {/* Connected Status */}
              <div className={`flex items-center gap-3 p-4 rounded-xl ${googleStatus.token_expired ? 'bg-orange-50 border border-orange-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                {googleStatus.token_expired ? (
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${googleStatus.token_expired ? 'text-orange-800' : 'text-emerald-800'}`}>
                    {googleStatus.token_expired ? "Session expirée" : "Compte connecté"}
                  </p>
                  <p className={`text-sm ${googleStatus.token_expired ? 'text-orange-600' : 'text-emerald-600'}`}>
                    {googleStatus.google_account_name || "Google Business"}
                  </p>
                  {googleStatus.google_account_id && (
                    <p className="text-xs text-slate-500 mt-1 font-mono">
                      {googleStatus.google_account_id}
                    </p>
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  <strong>Important :</strong> Ce compte Google est utilisé pour synchroniser les avis 
                  de tous les établissements clients. Assurez-vous d'être ajouté comme <strong>Manager</strong> 
                  sur chaque établissement Google Business des clients.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                {googleStatus.token_expired && (
                  <Button
                    onClick={handleConnectGoogle}
                    disabled={connecting}
                    className="rounded-xl bg-orange-500 hover:bg-orange-600"
                  >
                    {connecting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Reconnecter
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleDisconnectGoogle}
                  disabled={disconnecting}
                  className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                >
                  {disconnecting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Link2Off className="w-4 h-4 mr-2" />
                  )}
                  Déconnecter
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Not Connected */}
              <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                <Link2Off className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-700">Non connecté</p>
                  <p className="text-sm text-slate-500">Connectez votre compte Google Business pour synchroniser les avis</p>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h3 className="font-medium text-amber-800 mb-2">Étapes :</h3>
                <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                  <li>Cliquez sur "Connecter Google Business" ci-dessous</li>
                  <li>Autorisez l'accès à votre compte Google</li>
                  <li>Les clients doivent vous ajouter comme Manager sur leur établissement</li>
                  <li>Vous pourrez ensuite synchroniser leurs avis depuis "Établissements"</li>
                </ol>
              </div>

              {/* Connect Button */}
              <Button
                onClick={handleConnectGoogle}
                disabled={connecting}
                className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 h-12"
              >
                {connecting ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Link2 className="w-5 h-5 mr-2" />
                )}
                Connecter Google Business
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Aide & Documentation</h2>
        
        <div className="space-y-3">
          <a 
            href="https://business.google.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
          >
            <ExternalLink className="w-5 h-5 text-slate-500" />
            <span className="text-slate-700">Accéder à Google Business Profile</span>
          </a>
          
          <a 
            href="https://support.google.com/business/answer/3403100" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
          >
            <ExternalLink className="w-5 h-5 text-slate-500" />
            <span className="text-slate-700">Comment ajouter un Manager sur Google Business</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
